import { MANAGER_CALL_ACTIONS } from "@regal-voice/shared-types";
import { useSelector } from "react-redux";

import { useFlags } from "Services/FeatureFlagService";
import { selectConferenceSnapshotAgentCallSid } from "Services/state/conferences/ConferenceSnapshotSelectors";
import { selectActiveConferenceTask, selectConference } from "Services/state/conferences/Selectors";
import { selectActiveAction } from "Services/state/manager-action/Selectors";
import { isPendingInboundConferenceTask } from "Services/Task.service";

import { CallBarLayout, IncomingCallBarButtons, ActiveCallBarButtons } from "./components";
import { useBarControlsVisibility, useCallActionAcceptTask, useLegacyActiveCallEvents } from "./hooks";

const useCallBarData = () => {
    const { enableAdminTakeOverOnAiCalls } = useFlags();

    const activeConferenceTask = useSelector(selectActiveConferenceTask);
    const isIncomingCall = isPendingInboundConferenceTask(activeConferenceTask);
    const agentCallSid = useSelector(selectConferenceSnapshotAgentCallSid);
    const visible = useBarControlsVisibility({
        activeConferenceTask,
        deviceCallSid: agentCallSid,
    });
    const activeManagerAction = useSelector(selectActiveAction);
    const {
        connectionParams: { Params },
    } = useSelector(selectConference);

    const callParamsHasManagerAction = Params && Params.includes("managerAction");

    const hideCallBar =
        !visible ||
        !!(activeManagerAction && activeManagerAction !== MANAGER_CALL_ACTIONS.takeOver) ||
        // When admins can perform manager takeover on AI agents' calls, if the admin was listening first and then decides to take over,
        // the call params will not be able to reflect that change (they're only created when the admin's call *starts*).
        // As a result, only the manager action state managed directly by the UI is fully reliable
        (callParamsHasManagerAction && !enableAdminTakeOverOnAiCalls);

    // This hook populates conference.connectionParams.taskSid which selectActiveConferenceTask
    // depends on — required for incoming calls to appear before the Twilio device connects.
    useLegacyActiveCallEvents({ activeConferenceTask });

    const { acceptActiveTask, acceptingCall } = useCallActionAcceptTask({
        activeConferenceTask,
        deviceCallSid: agentCallSid,
    });

    return {
        hideCallBar,
        isIncomingCall,
        activeConferenceTask,
        acceptActiveTask,
        acceptingCall,
    };
};

export function CallBar(): JSX.Element {
    const { hideCallBar, activeConferenceTask, acceptActiveTask, acceptingCall, isIncomingCall } = useCallBarData();
    if (hideCallBar) {
        return <></>;
    }

    return (
        <CallBarLayout hideElementsOnSmallScreen={!isIncomingCall}>
            {isIncomingCall ? (
                <IncomingCallBarButtons
                    activeTask={activeConferenceTask}
                    acceptActiveTask={acceptActiveTask}
                    acceptingCall={acceptingCall}
                />
            ) : (
                <ActiveCallBarButtons />
            )}
        </CallBarLayout>
    );
}
